import React, { useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Check, ArrowRight, Download, Battery, ExternalLink, Car, Truck, Tractor, Sun, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { brandsData } from '../data/mock';

// Custom icons for Selenide product categories
const LCVIcon = ({ size = 24, className = "" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M10 17h4V5H2v12h3"/>
    <path d="M20 17h2v-3.34a4 4 0 0 0-1.17-2.83L19 9h-5v8h1"/>
    <circle cx="7.5" cy="17.5" r="2.5"/>
    <circle cx="17.5" cy="17.5" r="2.5"/>
  </svg>
);

const HCVIcon = ({ size = 24, className = "" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M1 3h15v13H1z"/>
    <path d="M16 8h4l3 3v5h-7V8z"/>
    <circle cx="5.5" cy="18.5" r="2.5"/>
    <circle cx="18.5" cy="18.5" r="2.5"/>
  </svg>
);

const ERickshawIcon = ({ size = 24, className = "" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M12 2L8 6v4H4l-2 4v4h2"/>
    <path d="M18 10h2l2 4v4h-2"/>
    <circle cx="6" cy="18" r="2"/>
    <circle cx="18" cy="18" r="2"/>
    <path d="M8 18h8"/>
    <path d="M8 6h8l4 4"/>
  </svg>
);

const InverterIcon = ({ size = 24, className = "" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <rect x="2" y="6" width="20" height="12" rx="2"/>
    <path d="M6 10v4"/>
    <path d="M10 10v4"/>
    <path d="M14 10h4"/>
    <path d="M16 8v4"/>
    <path d="M6 2v4"/>
    <path d="M18 2v4"/>
  </svg>
);

// Icon mapping for Selenide portfolio items
const getSelenideIcon = (itemName) => {
  const name = itemName.toLowerCase();
  if (name.includes('passenger') || name.includes('car')) return Car;
  if (name.includes('lcv') || name.includes('light commercial')) return LCVIcon;
  if (name.includes('hcv') || name.includes('heavy commercial')) return HCVIcon;
  if (name.includes('tractor')) return Tractor;
  if (name.includes('solar')) return Sun;
  if (name.includes('rickshaw') || name.includes('e-rickshaw')) return ERickshawIcon;
  if (name.includes('inverter') || name.includes('tubular')) return InverterIcon;
  return Battery;
};

const ProductsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  const activeTab = useMemo(() => {
    const brand = searchParams.get('brand');
    if (brand && ['gulf', 'surya', 'selenide'].includes(brand)) {
      return brand;
    }
    return 'gulf';
  }, [searchParams]);

  const handleTabChange = (value) => {
    setSearchParams({ brand: value });
  };

  const renderBrandContent = (brand) => {
    const data = brandsData[brand];
    
    return (
      <div className="space-y-12">
        {/* Brand Header with Product Image */}
        <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="flex justify-center">
              <div className="bg-gray-50 rounded-2xl p-4 w-full">
                <img
                  src={data.productImage}
                  alt={`${data.name} Products`}
                  className="w-full h-auto object-contain rounded-lg"
                />
              </div>
            </div>
            <div className="space-y-6">
              <div className="w-40 h-24 flex items-center">
                <img
                  src={data.logo}
                  alt={data.name}
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <p className="text-xl text-[#c41e3a] font-semibold">
                {data.tagline}
              </p>
              <p className="text-gray-600 leading-relaxed">
                {data.description}
              </p>
              <div className="inline-flex items-center px-4 py-2 bg-[#1a237e]/10 rounded-full text-[#1a237e] text-sm font-medium">
                <Battery size={16} className="mr-2" />
                Segment: {data.segment}
              </div>
            </div>
          </div>
        </div>

        {/* Brand Specific Content */}
        {brand === 'gulf' && (
          <>
            {/* Key Benefits */}
            <div className="bg-gray-50 rounded-2xl p-8 md:p-12">
              <h3 className="text-2xl font-bold text-[#1a237e] mb-8">Key Benefits</h3>
              <div className="grid md:grid-cols-2 gap-6">
                {data.benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-4 bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <Check size={18} className="text-green-600" />
                    </div>
                    <p className="text-gray-700 font-medium pt-1">{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {brand === 'surya' && (
          <>
            {/* Features */}
            <div className="bg-gray-50 rounded-2xl p-8 md:p-12">
              <h3 className="text-2xl font-bold text-[#1a237e] mb-8">Product Features</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {data.features.map((feature, index) => (
                  <Card key={index} className="border-0 shadow-sm hover:shadow-md transition-shadow bg-white">
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                          <Check size={18} className="text-amber-600" />
                        </div>
                        <p className="text-gray-700 text-sm">{feature}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Why Choose Surya */}
            <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg">
              <h3 className="text-2xl font-bold text-[#1a237e] mb-8">Why Choose SURYA Batteries</h3>
              <div className="grid md:grid-cols-2 gap-6">
                {data.whyChoose.map((reason, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-[#c41e3a]/10 rounded-full flex items-center justify-center text-[#c41e3a] font-bold text-sm">
                      {index + 1}
                    </div>
                    <p className="text-gray-700 pt-1">{reason}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* E-Rickshaw Segment */}
            <div className="bg-[#1a237e] text-white rounded-2xl p-8 md:p-12">
              <h3 className="text-2xl font-bold mb-6">{data.segment}</h3>
              <div className="space-y-4 mb-8">
                {data.segmentBenefits.map((benefit, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-amber-400 rounded-full" />
                    <span className="text-blue-100">{benefit}</span>
                  </div>
                ))}
              </div>
              <p className="text-amber-300 font-medium">{data.idealFor}</p>
            </div>
          </>
        )}

        {brand === 'selenide' && (
          <>
            {/* Product Portfolio */}
            <div className="bg-gray-50 rounded-2xl p-8 md:p-12">
              <h3 className="text-2xl font-bold text-[#1a237e] mb-8">Product Portfolio</h3>
              <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
                {data.portfolio.map((item, index) => {
                  const itemName = typeof item === 'string' ? item : item.name;
                  const itemUrl = typeof item === 'object' ? item.url : null;
                  const IconComponent = getSelenideIcon(itemName);
                  
                  const cardContent = (
                    <Card className={`border-0 shadow-sm hover:shadow-lg transition-all duration-300 group bg-white ${itemUrl ? 'cursor-pointer' : ''}`}>
                      <CardContent className="p-6 text-center">
                        <div className="w-12 h-12 bg-[#1a237e]/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-[#1a237e] transition-colors">
                          <IconComponent size={24} className="text-[#1a237e] group-hover:text-white transition-colors" />
                        </div>
                        <p className="font-medium text-gray-700 group-hover:text-[#1a237e] transition-colors">{itemName}</p>
                        {itemUrl && (
                          <ExternalLink size={14} className="mx-auto mt-2 text-gray-400 group-hover:text-[#c41e3a] transition-colors" />
                        )}
                      </CardContent>
                    </Card>
                  );
                  
                  return itemUrl ? (
                    <a key={index} href={itemUrl} target="_blank" rel="noopener noreferrer">
                      {cardContent}
                    </a>
                  ) : (
                    <div key={index}>{cardContent}</div>
                  );
                })}
              </div>
            </div>

            {/* External Links */}
            <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg">
              <h3 className="text-2xl font-bold text-[#1a237e] mb-8">Explore Selenide Products</h3>
              <div className="grid md:grid-cols-2 gap-6">
                {data.externalLinks.map((link, index) => (
                  <a
                    key={index}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group"
                  >
                    <Card className="border-2 border-[#1a237e]/20 hover:border-[#1a237e] transition-colors bg-white">
                      <CardContent className="p-6 flex items-center justify-between">
                        <span className="font-semibold text-[#1a237e]">{link.label}</span>
                        <ExternalLink size={20} className="text-[#1a237e] group-hover:translate-x-1 transition-transform" />
                      </CardContent>
                    </Card>
                  </a>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Distribution Support */}
        <div className="bg-gray-50 rounded-2xl p-8 md:p-12">
          <h3 className="text-2xl font-bold text-[#1a237e] mb-8">
            Sen &amp; Co Distribution Support
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {data.support.map((item, index) => (
              <Card key={index} className="border-0 shadow-sm hover:shadow-md transition-shadow bg-white">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-[#c41e3a]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check size={20} className="text-[#c41e3a]" />
                  </div>
                  <p className="font-medium text-gray-700">{item}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* CTAs */}
        <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg text-center">
          <h3 className="text-2xl font-bold text-[#1a237e] mb-8">
            Interested in Our Products?
          </h3>
          <div className="flex flex-wrap justify-center gap-4">
            {data.ctas.map((cta, index) => (
              cta.type === 'download' ? (
                <a
                  key={index}
                  href={cta.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                >
                  <Button
                    size="lg"
                    className="bg-[#1a237e] hover:bg-[#0d1654] text-white px-8 py-6 text-lg"
                  >
                    {cta.label}
                    <Download size={20} className="ml-2" />
                  </Button>
                </a>
              ) : (
                <Link key={index} to={cta.href}>
                  <Button
                    size="lg"
                    className={`px-8 py-6 text-lg ${
                      cta.type === 'primary'
                        ? 'bg-[#c41e3a] hover:bg-[#a01830] text-white'
                        : 'bg-[#1a237e] hover:bg-[#0d1654] text-white'
                    }`}
                  >
                    {cta.label}
                    <ArrowRight size={20} className="ml-2" />
                  </Button>
                </Link>
              )
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white">
      {/* Hero Section with Banner */}
      <section className="relative text-white h-[400px] overflow-hidden">
        {/* Banner Image */}
        <img 
          src="https://customer-assets.emergentagent.com/job_176ab01e-388f-4bea-9c6b-49caaa144cc8/artifacts/360xdroc_ProductBanner.png"
          alt="Products Banner"
          className="absolute inset-0 w-full h-full object-contain bg-[#1a237e]"
        />
        {/* Light overlay for text readability */}
        <div className="absolute inset-0 bg-black/40" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 h-full flex items-center justify-start">
          <div className="text-left max-w-xl">
            <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold mb-6 drop-shadow-lg">
              Our Product Portfolio
            </h1>
            <p className="text-xl text-white/90 drop-shadow">
              Sen &amp; Co offers a comprehensive range of automotive and energy storage solutions, catering to diverse applications.
            </p>
          </div>
        </div>
      </section>

      {/* Products Content */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
            {/* Square Card Style Brand Navigation */}
            <div className="flex justify-center mb-12">
              <div className="grid grid-cols-3 gap-6 max-w-3xl w-full">
                <button
                  onClick={() => handleTabChange('gulf')}
                  className={`group p-6 bg-white border-2 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 flex flex-col items-center justify-center space-y-3 ${
                    activeTab === 'gulf' 
                      ? 'border-[#1a237e] bg-[#1a237e]/5 shadow-lg ring-2 ring-[#1a237e]' 
                      : 'border-gray-100 hover:border-[#1a237e]/50'
                  }`}
                >
                  <div className={`w-24 h-24 flex items-center justify-center bg-gray-50 rounded-xl p-3 transition-transform duration-300 ${
                    activeTab === 'gulf' ? 'scale-105' : 'group-hover:scale-105'
                  }`}>
                    <img src={brandsData.gulf.logo} alt="Gulf" className="max-w-full max-h-full object-contain" />
                  </div>
                  <span className={`text-sm font-medium transition-colors ${
                    activeTab === 'gulf' ? 'text-[#1a237e]' : 'text-gray-500 group-hover:text-[#1a237e]'
                  }`}>
                    {brandsData.gulf.segment}
                  </span>
                </button>

                <button
                  onClick={() => handleTabChange('surya')}
                  className={`group p-6 bg-white border-2 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 flex flex-col items-center justify-center space-y-3 ${
                    activeTab === 'surya' 
                      ? 'border-[#1a237e] bg-[#1a237e]/5 shadow-lg ring-2 ring-[#1a237e]' 
                      : 'border-gray-100 hover:border-[#1a237e]/50'
                  }`}
                >
                  <div className={`w-24 h-24 flex items-center justify-center bg-gray-50 rounded-xl p-3 transition-transform duration-300 ${
                    activeTab === 'surya' ? 'scale-105' : 'group-hover:scale-105'
                  }`}>
                    <img src={brandsData.surya.logo} alt="Surya" className="max-w-full max-h-full object-contain" />
                  </div>
                  <span className={`text-sm font-medium transition-colors ${
                    activeTab === 'surya' ? 'text-[#1a237e]' : 'text-gray-500 group-hover:text-[#1a237e]'
                  }`}>
                    {brandsData.surya.segment}
                  </span>
                </button>

                <button
                  onClick={() => handleTabChange('selenide')}
                  className={`group p-6 bg-white border-2 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 flex flex-col items-center justify-center space-y-3 ${
                    activeTab === 'selenide' 
                      ? 'border-[#1a237e] bg-[#1a237e]/5 shadow-lg ring-2 ring-[#1a237e]' 
                      : 'border-gray-100 hover:border-[#1a237e]/50'
                  }`}
                >
                  <div className={`w-24 h-24 flex items-center justify-center bg-gray-50 rounded-xl p-3 transition-transform duration-300 ${
                    activeTab === 'selenide' ? 'scale-105' : 'group-hover:scale-105'
                  }`}>
                    <img src={brandsData.selenide.logo} alt="Selenide" className="max-w-full max-h-full object-contain" />
                  </div>
                  <span className={`text-sm font-medium transition-colors ${
                    activeTab === 'selenide' ? 'text-[#1a237e]' : 'text-gray-500 group-hover:text-[#1a237e]'
                  }`}>
                    {brandsData.selenide.segment}
                  </span>
                </button>
              </div>
            </div>

            <TabsContent value="gulf" className="mt-0">
              {renderBrandContent('gulf')}
            </TabsContent>
            <TabsContent value="surya" className="mt-0">
              {renderBrandContent('surya')}
            </TabsContent>
            <TabsContent value="selenide" className="mt-0">
              {renderBrandContent('selenide')}
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
};

export default ProductsPage;
